import {
  Card,
  CardContent,
  Box,
  Typography,
  Avatar,
  Skeleton,
  Tooltip,
  IconButton,
  Chip,
  styled,
  alpha,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";

interface EnhancedAnalyticsCardProps {
  title: string;
  value: string | number;
  icon?: React.ReactNode;
  trend?: {
    value: number;
    isPositive: boolean;
    label?: string;
  };
  subtitle?: string;
  color?: "primary" | "secondary" | "success" | "warning" | "error" | "info";
  loading?: boolean;
  error?: string;
  onClick?: () => void;
  onRefresh?: () => void;
  tooltip?: string;
  currency?: boolean;
  percentage?: boolean;
}

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "clickable" && prop !== "themeColor",
})<{ clickable?: boolean; themeColor?: string }>(
  ({ theme, clickable, themeColor }) => ({
    height: "100%",
    transition: "all 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
    cursor: clickable ? "pointer" : "default",
    position: "relative",
    overflow: "hidden",
    borderRadius: 16,
    border: "1px solid rgba(226, 232, 240, 0.8)",
    boxShadow: "none",
    background: "#FFFFFF",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: 3,
      background: `linear-gradient(90deg, ${themeColor || theme.palette.primary.main}, ${alpha(themeColor || theme.palette.primary.main, 0.4)})`,
      borderRadius: "16px 16px 0 0",
    },
    "&:hover": {
      transform: clickable ? "translateY(-4px)" : "translateY(-2px)",
      boxShadow: `0 12px 40px ${alpha(themeColor || theme.palette.primary.main, 0.12)}, 0 4px 12px rgba(0, 0, 0, 0.04)`,
      borderColor: alpha(themeColor || theme.palette.primary.main, 0.2),
    },
  })
);

const IconWrapper = styled(Avatar, {
  shouldForwardProp: (prop) => prop !== "bgColor",
})<{ bgColor?: string }>(({ theme, bgColor }) => ({
  width: 52,
  height: 52,
  borderRadius: 14,
  backgroundColor: bgColor
    ? alpha(bgColor, 0.08)
    : alpha(theme.palette.primary.main, 0.08),
  color: bgColor || theme.palette.primary.main,
  "& svg": {
    fontSize: 24,
  },
}));

export const EnhancedAnalyticsCard = ({
  title,
  value,
  icon,
  trend,
  subtitle,
  color = "primary",
  loading = false,
  error,
  onClick,
  onRefresh,
  tooltip,
  currency = false,
  percentage = false,
}: EnhancedAnalyticsCardProps) => {
  const getColorValue = (colorName: string) => {
    const colorMap: Record<string, string> = {
      primary: "#D58E4E",
      secondary: "#032961",
      success: "#22C55E",
      warning: "#F59E0B",
      error: "#EF4444",
      info: "#3B82F6",
    };
    return colorMap[colorName] || colorMap.primary;
  };

  const themeColor = getColorValue(color);

  const formatValue = (val: string | number) => {
    if (loading) return "";
    if (currency && typeof val === "number") {
      return new Intl.NumberFormat("en-SA", {
        style: "currency",
        currency: "SAR",
        minimumFractionDigits: 0,
      }).format(val);
    }
    if (percentage && typeof val === "number") {
      return `${val}%`;
    }
    if (typeof val === "number" && val >= 1000) {
      return new Intl.NumberFormat("en").format(val);
    }
    return val;
  };

  if (error) {
    return (
      <StyledCard themeColor="#EF4444">
        <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box>
              <Typography
                color="error"
                variant="body2"
                fontWeight={600}
              >
                Error loading data
              </Typography>
              <Typography
                color="text.secondary"
                variant="caption"
                sx={{ mt: 0.5, display: "block" }}
              >
                {error}
              </Typography>
            </Box>
            {onRefresh && (
              <IconButton size="small" onClick={onRefresh} color="error">
                <RefreshIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </CardContent>
      </StyledCard>
    );
  }

  return (
    <StyledCard
      clickable={!!onClick}
      onClick={onClick}
      themeColor={themeColor}
      elevation={0}
    >
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          {/* Left Side - Content */}
          <Box flex={1}>
            <Box display="flex" alignItems="center" gap={0.75} mb={1.5}>
              <Typography
                color="text.secondary"
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: "13px",
                  letterSpacing: "0.01em",
                }}
              >
                {loading ? <Skeleton width={100} /> : title}
              </Typography>
              {tooltip && !loading && (
                <Tooltip title={tooltip} arrow placement="top">
                  <InfoOutlinedIcon
                    sx={{
                      fontSize: 15,
                      color: "#CBD5E1",
                      cursor: "help",
                      "&:hover": { color: "#94A3B8" },
                    }}
                  />
                </Tooltip>
              )}
            </Box>

            {/* Value */}
            <Typography
              variant="h4"
              component="div"
              sx={{
                fontWeight: 800,
                fontSize: "28px",
                letterSpacing: "-0.03em",
                color: "#0F172A",
                mb: 1,
                lineHeight: 1.1,
              }}
            >
              {loading ? <Skeleton width={120} /> : formatValue(value)}
            </Typography>

            {/* Subtitle or Trend */}
            <Box display="flex" alignItems="center" gap={1}>
              {trend && !loading && (
                <Chip
                  size="small"
                  icon={
                    trend.isPositive ? (
                      <TrendingUpIcon sx={{ fontSize: "14px !important" }} />
                    ) : (
                      <TrendingDownIcon sx={{ fontSize: "14px !important" }} />
                    )
                  }
                  label={`${trend.isPositive ? "+" : ""}${trend.value}%`}
                  sx={{
                    height: 24,
                    fontWeight: 700,
                    fontSize: "11px",
                    borderRadius: "6px",
                    backgroundColor: trend.isPositive
                      ? alpha("#22C55E", 0.1)
                      : alpha("#EF4444", 0.1),
                    color: trend.isPositive ? "#16A34A" : "#DC2626",
                    "& .MuiChip-icon": {
                      color: "inherit",
                    },
                  }}
                />
              )}
              {trend?.label && !loading && (
                <Typography
                  variant="caption"
                  sx={{ color: "#94A3B8", fontSize: "12px" }}
                >
                  {trend.label}
                </Typography>
              )}
              {subtitle && !loading && !trend && (
                <Typography
                  variant="body2"
                  sx={{ color: "#94A3B8", fontSize: "12px" }}
                >
                  {subtitle}
                </Typography>
              )}
              {loading && <Skeleton width={80} />}
            </Box>
          </Box>

          {/* Right Side - Icon */}
          {icon && (
            <IconWrapper bgColor={themeColor}>
              {loading ? (
                <Skeleton
                  variant="rounded"
                  width={52}
                  height={52}
                  sx={{ borderRadius: "14px" }}
                />
              ) : (
                icon
              )}
            </IconWrapper>
          )}
        </Box>
      </CardContent>
    </StyledCard>
  );
};

export default EnhancedAnalyticsCard;

export { };
